/**
 * PPR-3 A · 上架中心「同行站」页签（样稿 designs/mockups/channel-center/上架中心-同行站与各渠道显示-样稿.html ①）
 *
 * 右：同行站展示清单——首图 / 产品 / 开关 / 建议售价起 / 结算底价起 / 原因 / 去补；筛选胶囊；拖动排序（原生拖放 + 上下移兜底）；保存 = PUT /api/v1/partner-site/products。
 * 左：以同行身份预览——选同行账号 → 新窗口打开同行站预览（2 小时只读令牌，不下单不扣额度；见 services/partner_preview.py）。
 * 底：「同行站上和别处不一样的地方」。价格两列只读（结算底价 = 售价管理中心「同行」渠道价；建议售价 = 「同行建议零售价」），同行站不另设价格。
 */
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowDown, ArrowUp, GripVertical, ImageOff } from "lucide-react";
import { Can, useCan } from "@/auth/permissions";
import { apiConfig } from "@/services/api";
import {
  PARTNER_SITE_QK, getPartnerSiteProducts, getPreviewPartners, openPartnerPreview, putPartnerSiteProducts, usdFromCents,
  type PartnerSiteItem,
} from "@/services/partnerSite";
import { useReorder } from "./useReorder";

type Filter = "all" | "showing" | "hidden" | "not_showing";
const FILTERS: { key: Filter; label: string }[] = [
  { key: "all", label: "全部" }, { key: "showing", label: "在展示" }, { key: "hidden", label: "已隐藏" }, { key: "not_showing", label: "没展示（缺东西）" },
];
const img = (u: string) => (u.startsWith("http") ? u : `${apiConfig.base}${u}`);

function Pill({ tone, children }: { tone: "green" | "amber" | "gray"; children: React.ReactNode }) {
  const cls = { green: "bg-emerald-50 text-emerald-600", amber: "bg-amber-50 text-amber-600", gray: "bg-slate-100 text-slate-600" }[tone];
  return <span className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-[12px] font-medium ${cls}`}><span className="h-[7px] w-[7px] rounded-full bg-current" />{children}</span>;
}

// ───────────────────────────── 左：以同行身份预览 ─────────────────────────────

export function PartnerPreviewPanel({ defaultPartnerId }: { defaultPartnerId?: string }) {
  const canManage = useCan("listing.mappings.manage");
  const q = useQuery({ queryKey: PARTNER_SITE_QK.previewPartners, queryFn: getPreviewPartners, enabled: canManage });
  const [sel, setSel] = useState("");
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const partners = q.data ?? [];
  useEffect(() => {
    if (sel || partners.length === 0) return;
    const first = partners.find((p) => p.partner_id === defaultPartnerId && p.previewable) ?? partners.find((p) => p.previewable);
    if (first) setSel(first.partner_id);
  }, [partners, sel, defaultPartnerId]);
  const cur = partners.find((p) => p.partner_id === sel);
  const open = async () => {
    setErr(null); setBusy(true);
    try { await openPartnerPreview(sel); } catch (e) { setErr(e instanceof Error ? e.message : "预览打开失败，请重试"); } finally { setBusy(false); }
  };
  return (
    <div data-testid="partner-preview-panel" className="flex flex-col gap-3 rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
      <span className="self-start rounded-full bg-amber-100 px-2.5 py-0.5 text-[12px] text-amber-800">预览 · 以同行身份看 · 不下单不扣额度</span>
      <h3 className="text-[15px] font-semibold text-slate-900">同行站 · 报价与空位</h3>
      {!canManage ? (
        <p className="text-[12.5px] text-slate-400">以同行身份预览需要「上架中心 · 管理」权限。</p>
      ) : q.isError ? (
        <button type="button" onClick={() => q.refetch()} className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700">同行列表加载失败，点击重试</button>
      ) : (
        <>
          <label className="flex flex-col gap-1 text-[12.5px] text-slate-600">选一个同行账号看
            <select aria-label="选同行" value={sel} onChange={(e) => setSel(e.target.value)} className="rounded border border-slate-300 px-2 py-1.5 text-sm">
              <option value="">{q.isLoading ? "加载中…" : "请选择同行"}</option>
              {partners.map((p) => <option key={p.partner_id} value={p.partner_id} disabled={!p.previewable}>{p.name_cn}{p.previewable ? "" : `（${p.reason}）`}</option>)}
            </select>
          </label>
          <button type="button" data-testid="open-partner-preview" disabled={!cur?.previewable || busy} onClick={open}
            className="rounded-md bg-blue-600 px-3.5 py-2 text-[13px] font-medium text-white disabled:opacity-50">{busy ? "正在打开…" : "在新窗口打开同行站预览 ↗"}</button>
          {err && <p className="text-xs text-rose-600">{err}</p>}
        </>
      )}
      <p className="text-[12px] leading-relaxed text-slate-400">同行站是电脑宽表，预览在新窗口打开整页；用所选同行的额度 / 等级显示价格，只看不下单（订单、对账单、额度在预览里不显示）。预览链接 2 小时有效，别发给同行。</p>
    </div>
  );
}

// ───────────────────────────── 右：展示清单 ─────────────────────────────

export function PartnerSiteTab({ focusProductId }: { focusProductId?: string | null }) {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: PARTNER_SITE_QK.list, queryFn: getPartnerSiteProducts });
  const [draft, setDraft] = useState<PartnerSiteItem[] | null>(null);
  const [filter, setFilter] = useState<Filter>("all");
  const [err, setErr] = useState<string | null>(null);
  const items = draft ?? q.data?.items ?? [];
  const dirty = draft !== null;
  const { move, dragProps } = useReorder(items, (v) => setDraft(v));
  const saveMut = useMutation({
    mutationFn: () => {
      // 在展示 / 已隐藏的行按当前顺序编号 1..n；没展示（缺东西）的行不动（保持原序号）——它们补齐后会按原序号排
      let n = 0;
      return putPartnerSiteProducts(items.map((i) => ({ product_id: i.product_id, partner_visible: i.partner_visible, partner_sort: i.display_status === "not_showing" ? i.partner_sort : ++n })));
    },
    onSuccess: (d) => { qc.setQueryData(PARTNER_SITE_QK.list, d); setDraft(null); setErr(null); },
    onError: (e) => setErr(e instanceof Error ? e.message : "保存失败，请重试"),
  });
  const counts = useMemo(() => ({
    all: items.length, showing: items.filter((i) => i.display_status === "showing").length,
    hidden: items.filter((i) => i.display_status === "hidden").length, not_showing: items.filter((i) => i.display_status === "not_showing").length,
  }), [items]);
  const shown = filter === "all" ? items : items.filter((i) => i.display_status === filter);
  const focus = focusProductId ?? null;
  useEffect(() => { if (focus) document.getElementById(`partner-row-${focus}`)?.scrollIntoView({ block: "center" }); }, [focus, q.data]);

  const toggle = (id: string) => setDraft(items.map((i) => {
    if (i.product_id !== id) return i;
    const visible = !i.partner_visible;
    // 本地预判展示状态：开关打开且没有缺东西原因 → showing；关 → hidden（保存后以服务端返回为准）
    const ds = !visible ? "hidden" : i.reason_code && i.reason_code !== "manual_off" ? "not_showing" : "showing";
    return { ...i, partner_visible: visible, display_status: ds, reason_code: ds === "hidden" ? "manual_off" : i.reason_code === "manual_off" ? null : i.reason_code, reason_label: ds === "hidden" ? "已隐藏（手动关）" : i.reason_code === "manual_off" ? null : i.reason_label };
  }));

  if (q.isLoading) return <div className="rounded-xl border border-slate-200 bg-white p-8 text-center text-[13px] text-slate-500">加载中...</div>;
  if (q.isError) return <button type="button" onClick={() => q.refetch()} className="rounded-xl border border-rose-200 bg-rose-50 p-4 text-left text-[13px] font-medium text-rose-700">同行站清单加载失败，点击重试</button>;

  return (
    <div className="flex flex-col gap-4" data-testid="partner-site-tab">
      <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
        <h3 className="text-[15px] font-semibold text-slate-900">同行站展示清单</h3>
        <p className="mt-0.5 text-[12.5px] text-slate-500">所有已发布产品在同行站上的情况 · 拖动排顺序（= 同行站大表 / 线路产品「综合」排序）· 开关只管同行站，不影响手机站和平台 · 没展示的写原因，点「去补」跳到那条产品</p>
        <div className="mt-3 flex flex-wrap gap-1.5" role="tablist">
          {FILTERS.map((f) => (
            <button key={f.key} type="button" data-testid={`pf-${f.key}`} onClick={() => setFilter(f.key)}
              className={`rounded-full border px-3 py-1 text-[12.5px] ${filter === f.key ? "border-blue-600 bg-blue-600 font-semibold text-white" : "border-slate-200 bg-white text-slate-600 hover:bg-slate-50"}`}>
              {f.label} <b>{counts[f.key]}</b>
            </button>
          ))}
        </div>
        {filter !== "all" && <p className="mt-2 text-[12px] text-slate-400">筛选状态下不能拖动排序，切回「全部」再排。</p>}
        <table className="mt-3 w-full text-[13px]">
          <thead><tr className="border-b border-slate-200 text-left text-[12px] text-slate-500"><th className="w-[92px] py-2">顺序</th><th>产品</th><th>同行站</th><th>建议售价起 / 人</th><th>结算底价起 / 人</th><th>原因</th><th /></tr></thead>
          <tbody>
            {shown.length === 0 && <tr><td colSpan={7} className="py-6 text-center text-slate-400">没有符合的产品</td></tr>}
            {shown.map((it) => {
              const idx = items.findIndex((x) => x.product_id === it.product_id);
              const sortable = filter === "all" && it.display_status !== "not_showing";
              const rank = items.slice(0, idx + 1).filter((x) => x.display_status !== "not_showing").length;
              return (
                <tr key={it.product_id} id={`partner-row-${it.product_id}`} data-testid="partner-row" {...(sortable ? dragProps(idx) : {})}
                  className={`border-b border-slate-100 ${focus === it.product_id ? "bg-blue-50/60" : ""}`}>
                  <td className="py-2.5">
                    {it.display_status === "not_showing" ? <span className="text-slate-400">—</span> : (
                      <span className="inline-flex items-center gap-1 text-slate-400"><GripVertical size={14} className={sortable ? "cursor-grab" : "opacity-40"} />{rank}
                        <Can perm="listing.mappings.manage">
                          {sortable && <span className="ml-1 inline-flex gap-0.5">
                            <button type="button" aria-label="上移" onClick={() => move(idx, idx - 1)} className="rounded p-0.5 hover:bg-slate-100"><ArrowUp size={12} /></button>
                            <button type="button" aria-label="下移" onClick={() => move(idx, idx + 1)} className="rounded p-0.5 hover:bg-slate-100"><ArrowDown size={12} /></button>
                          </span>}
                        </Can>
                      </span>)}
                  </td>
                  <td>
                    <div className="flex items-center gap-2.5">
                      {it.cover_url ? <img src={img(it.cover_url)} alt="" className="h-9 w-12 rounded object-cover" /> : <span className="flex h-9 w-12 items-center justify-center rounded bg-slate-100 text-slate-300"><ImageOff size={14} /></span>}
                      <span><b>{it.name_cn}</b><div className="text-[11.5px] text-slate-400">{it.code}</div></span>
                    </div>
                  </td>
                  <td>
                    {it.display_status === "not_showing" ? <Pill tone="amber">没展示</Pill> : (
                      <Can perm="listing.mappings.manage" fallback={it.display_status === "showing" ? <Pill tone="green">在展示</Pill> : <Pill tone="gray">已隐藏</Pill>}>
                        <button type="button" role="switch" aria-checked={it.partner_visible} aria-label={`同行站开关 ${it.code}`} onClick={() => toggle(it.product_id)}
                          className={`h-[22px] w-10 rounded-full p-0.5 transition ${it.partner_visible ? "bg-blue-600" : "bg-slate-300"}`}>
                          <span className={`block h-[18px] w-[18px] rounded-full bg-white shadow transition ${it.partner_visible ? "translate-x-[18px]" : ""}`} />
                        </button>
                      </Can>)}
                  </td>
                  <td className="tabular-nums">{usdFromCents(it.retail_from_cents)}</td>
                  <td className="tabular-nums">{usdFromCents(it.settlement_from_cents)}</td>
                  <td className={it.display_status === "not_showing" ? "text-amber-600" : "text-slate-500"}>{it.reason_label ?? "—"}</td>
                  <td className="text-right">{it.display_status === "not_showing" && (
                    <Link to={`/products/${it.product_id}?tab=content`} className="rounded-md border border-slate-300 px-2 py-0.5 text-[12px] text-slate-600 hover:bg-slate-50">去补 ›</Link>)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {err && <p className="mt-2 text-xs text-rose-600">{err}</p>}
        <Can perm="listing.mappings.manage">
          <div className="mt-3 flex justify-end gap-2">
            <button type="button" disabled={!dirty} onClick={() => { setDraft(null); setErr(null); }} className="rounded-md border border-slate-300 bg-white px-3.5 py-1.5 text-[13px] disabled:opacity-50">取消</button>
            <button type="button" data-testid="partner-save" disabled={!dirty || saveMut.isPending} onClick={() => saveMut.mutate()} className="rounded-md bg-blue-600 px-3.5 py-1.5 text-[13px] font-medium text-white disabled:opacity-50">{saveMut.isPending ? "保存中…" : "保存"}</button>
          </div>
        </Can>
        <p className="mt-3 text-[12px] leading-relaxed text-slate-400">价格两列只读：结算底价 = 售价管理中心「同行」渠道价；建议售价 = 售价管理中心已有的「同行建议零售价」（同行站上只叫「建议售价」，不提佣金）。同行站不另设价格。</p>
      </div>

      <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
        <h3 className="text-[15px] font-semibold text-slate-900">同行站上和别处不一样的地方（只有这些在这里管）</h3>
        <p className="mt-1 text-[13px] leading-relaxed text-slate-600">① 给不给同行看（开关）　② 排第几（拖动）　③ 同行站首页公告 / 联系电话（已在系统设置里，原样保留）—— 其它全部读产品管理和图片库，不在这里改。</p>
      </div>
    </div>
  );
}
